import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { PARCEL_SERVICE_LABELS } from "./delivery-rules";
import type { ShipmentSelections } from "./shipping-api";

type Scope = "quote" | "shipment";
type Choices = Partial<ShipmentSelections>;
type Toggle =
  | "limited_access"
  | "liftgate"
  | "inside_delivery"
  | "appointment"
  | "construction_site";

const TOGGLES: ReadonlyArray<{
  field: Toggle;
  label: string;
  on: string;
  off: string;
  freightOnly: boolean;
}> = [
  {
    field: "limited_access",
    label: "Limited access",
    on: "Required",
    off: "Not required",
    freightOnly: true,
  },
  {
    field: "liftgate",
    label: "Liftgate",
    on: "Liftgate",
    off: "No liftgate",
    freightOnly: true,
  },
  {
    field: "inside_delivery",
    label: "Inside delivery",
    on: "Required",
    off: "Not required",
    freightOnly: true,
  },
  {
    field: "appointment",
    label: "Delivery appointment",
    on: "Required",
    off: "Not required",
    freightOnly: true,
  },
  {
    field: "construction_site",
    label: "Construction-site delivery",
    on: "Required",
    off: "Not required",
    freightOnly: true,
  },
];

function yesNo(value: boolean | null | undefined, on: string, off: string) {
  return value == null ? "By weight" : value ? on : off;
}

type Unloading = "inherit" | "none" | "dock" | "forklift";

function unloading(choices: Choices): Unloading {
  if (choices.has_loading_dock == null && choices.has_forklift == null)
    return "inherit";
  return choices.has_loading_dock
    ? "dock"
    : choices.has_forklift
      ? "forklift"
      : "none";
}

const UNLOADING_LABELS = {
  none: "No dock or forklift",
  dock: "Loading dock",
  forklift: "Forklift",
} as const;

/**
 * The delivery choices for the whole quote or one shipment, matching the old dropship quote
 * builder's delivery defaults. A shipment's "Quote setting" follows the quote; the quote's own
 * gaps follow address detection and the liftgate weight rule.
 */
export function DeliveryChoices({
  scope,
  value,
  inherited,
  detectionLabel,
  freight,
  parcel,
  disabled,
  onChange,
}: {
  scope: Scope;
  /** This scope's own choices; null fields inherit. */
  value: Choices;
  /** What a null field resolves to: the quote's resolved choices (shipment) or detection (quote). */
  inherited: Choices;
  detectionLabel: string;
  freight: boolean;
  parcel: boolean;
  disabled: boolean;
  onChange: (changes: Choices) => void;
}) {
  const inheritLabel = (shown: string) =>
    scope === "quote" ? `Detected (${shown})` : `Quote setting (${shown})`;
  const siteLabel =
    (inherited.destination_type ?? "commercial") === "residential"
      ? "Residential"
      : "Commercial";
  const unloadingValue = unloading(value);
  const inheritedUnloading = unloading(inherited);

  return (
    <div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
      {freight ? (
        <ChoiceRow label="Site type">
          <ReviewChoiceGroup
            disabled={disabled}
            label="Site type"
            onChange={(next) =>
              onChange({ destination_type: next === "inherit" ? null : next })
            }
            options={[
              {
                value: "inherit",
                label: scope === "quote" ? `Detected (${detectionLabel})` : inheritLabel(siteLabel),
              },
              { value: "commercial", label: "Commercial" },
              { value: "residential", label: "Residential" },
            ]}
            value={value.destination_type ?? "inherit"}
          />
        </ChoiceRow>
      ) : null}
      {TOGGLES.filter((toggle) => freight || !toggle.freightOnly).map(
        (toggle) => {
          const own = value[toggle.field];
          const shown = inherited[toggle.field];
          // The quote's extra services default to not required; only liftgate and limited access
          // have an automatic answer to fall back to.
          const automatic =
            toggle.field === "liftgate" || toggle.field === "limited_access";
          const options =
            scope === "quote" && !automatic
              ? [
                  { value: "no", label: toggle.off },
                  { value: "yes", label: toggle.on },
                ]
              : [
                  {
                    value: "inherit",
                    label:
                      scope === "quote"
                        ? toggle.field === "liftgate"
                          ? "By weight"
                          : `Detected (${shown ? "Yes" : "No"})`
                        : inheritLabel(
                            toggle.field === "liftgate"
                              ? yesNo(shown, toggle.on, toggle.off)
                              : shown
                                ? toggle.on
                                : toggle.off,
                          ),
                  },
                  { value: "yes", label: toggle.on },
                  { value: "no", label: toggle.off },
                ];
          const current =
            own == null
              ? scope === "quote" && !automatic
                ? "no"
                : "inherit"
              : own
                ? "yes"
                : "no";
          return (
            <ChoiceRow key={toggle.field} label={toggle.label}>
              <ReviewChoiceGroup
                disabled={disabled}
                label={toggle.label}
                onChange={(next) =>
                  onChange({
                    [toggle.field]: next === "inherit" ? null : next === "yes",
                  })
                }
                options={options}
                value={current}
              />
            </ChoiceRow>
          );
        },
      )}
      {freight ? (
        <ChoiceRow label="Unloading">
          <ReviewChoiceGroup
            disabled={disabled}
            label="Unloading"
            onChange={(next: Unloading) =>
              onChange(
                next === "inherit"
                  ? { has_loading_dock: null, has_forklift: null }
                  : {
                      has_loading_dock: next === "dock",
                      has_forklift: next === "forklift",
                    },
              )
            }
            options={[
              ...(scope === "shipment"
                ? [
                    {
                      value: "inherit" as const,
                      label: inheritLabel(
                        UNLOADING_LABELS[
                          inheritedUnloading === "inherit" ? "none" : inheritedUnloading
                        ],
                      ),
                    },
                  ]
                : []),
              { value: "none", label: UNLOADING_LABELS.none },
              { value: "dock", label: UNLOADING_LABELS.dock },
              { value: "forklift", label: UNLOADING_LABELS.forklift },
            ]}
            value={
              scope === "quote" && unloadingValue === "inherit"
                ? "none"
                : unloadingValue
            }
          />
        </ChoiceRow>
      ) : null}
      {parcel ? (
        <ChoiceRow label="Parcel service">
          <ReviewChoiceGroup
            disabled={disabled}
            label="Parcel service"
            onChange={(next) =>
              onChange({ parcel_service: next === "inherit" ? null : next })
            }
            options={[
              ...(scope === "shipment"
                ? [
                    {
                      value: "inherit" as const,
                      label: inheritLabel(
                        PARCEL_SERVICE_LABELS[inherited.parcel_service ?? "ground"],
                      ),
                    },
                  ]
                : []),
              { value: "ground", label: PARCEL_SERVICE_LABELS.ground },
              { value: "two_day", label: PARCEL_SERVICE_LABELS.two_day },
              { value: "next_day", label: PARCEL_SERVICE_LABELS.next_day },
            ]}
            value={
              value.parcel_service ?? (scope === "quote" ? "ground" : "inherit")
            }
          />
        </ChoiceRow>
      ) : null}
    </div>
  );
}

function ChoiceRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-w-0 gap-1 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
    </div>
  );
}
